import './GroupMembersModal.scss';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloseButton } from '@mantine/core';
import { MdSearch } from 'react-icons/md';
import { AddressBookService } from '@services/AddressBookService';
import { abookItemTypeLabel, IPersonCardMember } from '@models/addressbook/IPersonCard';
import UserAvatar from '@molecules/UserAvatar/UserAvatar';
import { useAvatarMeta } from '@molecules/UserAvatar/useAvatarMeta';
import UserHoverCard from '@molecules/UserHoverCard/UserHoverCard';
import Modal from '@srm/ui/modal';
import Preloader from '@srm/ui/preloader';
import Skeleton from '@srm/ui/skeleton';

const PAGE = 50;
const DEBOUNCE = 300;

export interface GroupMembersModalProps {
    opened: boolean;
    onClose: () => void;
    /** Ключ группы (userKey карточки). */
    groupKey: string;
    groupName: string;
    /** Тип элемента АК (workgroup/department/…): аватар группы — скруглённый квадрат. */
    groupType?: string;
    hasImage?: boolean;
    photoVersion?: string;
    /** Известное общее число участников (из карточки) — подпись до первой загрузки. */
    totalHint?: number;
}

const fmt = (n: number) => n.toLocaleString('ru-RU');

/** Подсветка совпадения с поисковым запросом в имени участника. */
function highlight(name: string, q: string): React.ReactNode {
    if (!q) return name;
    const i = name.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return name;
    return (
        <>
            {name.slice(0, i)}
            <mark className="gmm__mark">{name.slice(i, i + q.length)}</mark>
            {name.slice(i + q.length)}
        </>
    );
}

/**
 * Строка участника: аватар (мета фото батчится через useAvatarMeta) + имя с подсветкой.
 * Имя — триггер hover-карточки участника (как PersonLine в самой карточке): наведение
 * лениво подгружает его контакты по member.key. UserHoverCard самодостаточен.
 */
const MemberRow: React.FC<{ member: IPersonCardMember; query: string }> = ({ member, query }) => {
    const meta = useAvatarMeta(member.key);
    return (
        <div className="gmm__row">
            <UserAvatar
                userKey={member.key}
                name={member.name}
                hasImage={meta?.hasImage ?? false}
                photoVersion={meta?.photoVersion ?? undefined}
                type="user"
                size={28}
            />
            <div className="gmm__row-who">
                <UserHoverCard userKey={member.key} fallbackName={member.name} position="right" inline>
                    <span className="gmm__row-name">{highlight(member.name, query)}</span>
                </UserHoverCard>
            </div>
        </div>
    );
};

/**
 * Модалка «Все участники группы»: отдельная поверхность для просмотра состава группы,
 * которая может содержать тысячи человек. Серверная пагинация (подгрузка порциями по
 * скроллу) + поиск по имени с дебаунсом. Живёт независимо от hover-карточки, поэтому
 * курсор свободен для набора и прокрутки. Гонки ответов гасятся по reqId.
 */
const GroupMembersModal: React.FC<GroupMembersModalProps> = ({
    opened,
    onClose,
    groupKey,
    groupName,
    groupType,
    hasImage,
    photoVersion,
    totalHint,
}) => {
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<IPersonCardMember[]>([]);
    const [total, setTotal] = useState<number>(totalHint ?? 0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const reqId = useRef(0);
    const debTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

    const runLoad = useCallback(
        (skip: number, search: string, reset: boolean) => {
            const id = ++reqId.current;
            setLoading(true);
            setError(false);
            AddressBookService.fetchGroupMembers(groupKey, skip, PAGE, search)
                .then((r) => {
                    if (id !== reqId.current) return; // устаревший ответ — игнорируем
                    const data = r.data?.data ?? [];
                    setItems((prev) => (reset ? data : [...prev, ...data]));
                    setTotal(r.data?.totalCount ?? 0);
                })
                .catch(() => {
                    if (id === reqId.current) setError(true);
                })
                .finally(() => {
                    if (id === reqId.current) setLoading(false);
                });
        },
        [groupKey],
    );

    // Открытие / смена группы: сброс поиска и первая страница.
    useEffect(() => {
        if (!opened) return;
        setQuery('');
        setItems([]);
        setTotal(totalHint ?? 0);
        runLoad(0, '', true);
        return () => {
            if (debTimer.current) clearTimeout(debTimer.current);
        };
        // totalHint намеренно не в зависимостях — только реакция на открытие/группу
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, groupKey, runLoad]);

    const onQueryChange = useCallback(
        (val: string) => {
            setQuery(val);
            if (debTimer.current) clearTimeout(debTimer.current);
            debTimer.current = setTimeout(() => runLoad(0, val.trim(), true), DEBOUNCE);
        },
        [runLoad],
    );

    const onScroll = useCallback(
        (e: React.UIEvent<HTMLDivElement>) => {
            const el = e.currentTarget;
            if (loading || items.length >= total) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 80) {
                runLoad(items.length, query.trim(), false);
            }
        },
        [loading, items.length, total, query, runLoad],
    );

    const q = query.trim();
    const typeLabel = abookItemTypeLabel(groupType);

    return (
        <Modal opened={opened} onClose={onClose} width={560} withCloseButton={false} noFrame className="gmm">
            <div className="gmm__head">
                <UserAvatar
                    userKey={groupKey}
                    name={groupName}
                    hasImage={hasImage}
                    photoVersion={photoVersion}
                    type={groupType}
                    size={30}
                    radius="md"
                />
                <div className="gmm__title">
                    <div className="gmm__name">{groupName}</div>
                    <div className="gmm__sub">
                        {typeLabel ? `${typeLabel} · ` : ''}
                        {fmt(total)} участников
                    </div>
                </div>
                <CloseButton onClick={onClose} aria-label="Закрыть" />
            </div>

            <div className="gmm__search">
                <MdSearch size={18} className="gmm__search-ic" aria-hidden />
                <input
                    className="gmm__search-input"
                    value={query}
                    onChange={(e) => onQueryChange(e.currentTarget.value)}
                    placeholder="Поиск участника по имени…"
                    autoComplete="off"
                    data-autofocus
                />
                {query && <CloseButton size="sm" onClick={() => onQueryChange('')} aria-label="Очистить" />}
            </div>

            <div className="gmm__meta">
                {loading && items.length === 0 ? 'Загрузка…' : `показано ${fmt(items.length)} из ${fmt(total)}`}
            </div>

            <div className="gmm__list" onScroll={onScroll}>
                {loading && items.length === 0 && (
                    <div className="gmm__skeleton" aria-hidden="true">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <div className="gmm__row" key={i}>
                                <Skeleton width={28} height={28} circle />
                                <div className="gmm__row-who">
                                    <Skeleton height={13} width={`${46 + ((i * 11) % 34)}%`} radius="sm" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {error && <div className="gmm__empty">Не удалось загрузить участников. Попробуйте ещё раз.</div>}

                {!error && !loading && items.length === 0 && (
                    <div className="gmm__empty">
                        <div className="gmm__empty-big">Никого не нашли</div>
                        {q ? 'Проверьте запрос — возможно, опечатка.' : 'В группе нет участников.'}
                    </div>
                )}

                {items.map((m, i) => (
                    <MemberRow key={`${m.key}-${i}`} member={m} query={q} />
                ))}

                {loading && items.length > 0 && (
                    <div className="gmm__sentinel">
                        <Preloader size={16} position="inline" /> Загрузка ещё…
                    </div>
                )}
                {!loading && !error && items.length > 0 && items.length >= total && (
                    <div className="gmm__sentinel">Показаны все · {fmt(total)}</div>
                )}
            </div>
        </Modal>
    );
};

export default GroupMembersModal;
